import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { first, map, takeUntil, tap, withLatestFrom } from 'rxjs/operators';

import { ChangeCoverageFeatureFacade } from '@amfam/policy/change-coverage/feature';
import { ICoverage, PolicyCoverage, Rate } from '@amfam/policy/models';
import { PolicySharedService } from '@amfam/policy/shared';
import { FEATURES, VehicleCoveragesFacade } from '@amfam/policy/vehicle-coverages/data-access';
import { QuoteSummaryModel } from '@amfam/shared/models';
import { CoverageCurrencyUSDPipe } from '@amfam/shared/ui/pipes';
import { userQuery } from '@amfam/shared/user';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import {
  DockingBarService,
  DsModalService,
  LoadingSpinnerService,
  PageTitleService
} from '@amfam/ui-kit';

@Component({
  selector: 'amfam-change-coverage-summary',
  templateUrl: './change-coverage-summary.component.html',
  styleUrls: ['./change-coverage-summary.component.scss'],
  standalone: false
})
export class ChangeCoverageSummaryComponent implements OnInit, OnDestroy {
  @Output() errorEvent = new EventEmitter<{
    hasError: boolean;
    errorMessage: string;
  }>();

  isPartner$: Observable<boolean> = this.store.select(BrandSelectors.selectIsPartner);
  state$: Observable<string> = this.store.select(userQuery.selectUserStateCode);
  isPartner: boolean;
  vehicleCoveragesSummaryList$: Observable<QuoteSummaryModel[]> = combineLatest([
    this.changeCoverageFeatureFacade.initialCoverages$,
    this.vehicleCoveragesFacade.coverages$,
    this.isPartner$,
    this.state$
  ]).pipe(
    map(([initCov, chanCov, isPartner, state]: [ICoverage[], ICoverage[], boolean, string]) =>
      this.coverageReducer(chanCov, false, isPartner, state, initCov)
    ),
    map(list => list.filter(l => l.value !== 'Not Included'))
  );
  policyCoveragesSummaryList$: Observable<QuoteSummaryModel[]> = combineLatest([
    this.vehicleCoveragesFacade.policyCoverages$,
    this.isPartner$,
    this.state$
  ]).pipe(
    map(([r, isPartner, state]) => this.coverageReducer(r, true, isPartner, state)),
    map(list => list.filter(l => l.value !== 'Not Included'))
  );
  discountsOnCoveragesSummaryList$: Observable<QuoteSummaryModel[]> =
    this.vehicleCoveragesFacade.appliedDiscounts$.pipe(
      tap(
        appliedDiscounts =>
          (this.hasAntiTheftDiscount$ = of(
            !!appliedDiscounts.find(
              appliedDiscount => appliedDiscount.discountCode === 'PAAntiTheftDisc_af'
            )
          ))
      ),
      map(discounts =>
        discounts.map(d => ({
          option: d.name,
          value: 'Applied'
        }))
      )
    );
  hasAntiTheftDiscount$: Observable<boolean>;
  vehicleDescription$: Observable<string> = this.vehicleCoveragesFacade.vehicleName$;
  vehicles$ = this.vehicleCoveragesFacade.vehicles$;
  vehicleRate$: Observable<Rate> = this.vehicleCoveragesFacade.rate$;
  public vehicleId: string;
  drivers: [];

  icons$ = this.vehicles$.pipe(
    map(vehicles => vehicles.length),
    map(count => ({
      vehicleIconCount: Array(count > 3 ? 1 : count)
        .fill(1)
        .map((_c, index) => {
          if (count === 2 && index === 1) {
            return 'medium-icon-size';
          } else if (count > 3) {
            return 'large-icon-size';
          }
          return 'normal-icon-size';
        }),
      vehicleRestNumber: count - 1
    }))
  );
  authorized$: Observable<boolean>;

  private stop$: Subject<void> = new Subject<void>();
  private changeCoverageText = this.copyService.getCopy('policy.changeCoverage.pageTitle');
  constructor(
    public vehicleCoveragesFacade: VehicleCoveragesFacade,
    private changeCoverageFeatureFacade: ChangeCoverageFeatureFacade,
    private featureService: FeatureFlagService,
    private store: Store,
    private route: ActivatedRoute,
    private spinnerService: LoadingSpinnerService,
    protected modalService: DsModalService,
    private pageTitle: PageTitleService,
    private dockingBarService: DockingBarService,
    private copyService: CopyService,
    private coveageChangeFeatureFacade: ChangeCoverageFeatureFacade,
    private policySharedService: PolicySharedService
  ) {}

  ngOnDestroy(): void {
    this.stop$.next();
    this.stop$.complete();
  }

  onlineBind() {
    this.spinnerService.start({
      loadingMessage: `Updating Your Coverage`,
      secondaryMessage: 'This will take a moment',
      blockActions: true,
      isNewLoader: true
    });
    this.vehicleCoveragesFacade.bindChangeCoverage(this.vehicleId);
    if (!this.isPartner) {
      this.coveageChangeFeatureFacade.sendAnalytics({
        buttonAnalyticName: 'Quote:ConfirmUpdate',
        event: FEATURES.CHANGE
      });
    }
  }

  ngOnInit(): void {
    this.vehicleCoveragesFacade.clearError();
    this.vehicleCoveragesFacade.error$
      .pipe(
        withLatestFrom(this.isPartner$),
        takeUntil(this.stop$),
        tap(([error, isPartner]) => {
          if (error) {
            this.spinnerService.stop();
            this.errorEvent.emit({
              hasError: true,
              errorMessage: this.copyService.getCopy(
                isPartner
                  ? 'policy.changeCoverage.changeCoverageErrorMessageForPartner'
                  : 'policy.changeCoverage.changeCoverageErrorMessage'
              )
            });
          }
        })
      )
      .subscribe();

    this.isPartner$
      .pipe(
        first(),
        tap(partner => {
          this.isPartner = partner;
          this.dockingBarService.registerHeading(
            this.copyService.getCopy('policy.changeCoverage.summary.summaryPageHeaderEditCoverage')
          );

          if (this.isPartner) {
            this.changeCoverageFeatureFacade.sendAnalytics({
              event: FEATURES.CHANGE,
              pageAnalyticName: 'Review'
            });
          } else {
            this.changeCoverageFeatureFacade.sendAnalytics({
              event: FEATURES.CHANGE,
              pageAnalyticName: 'Quote:ConfirmUpdate'
            });
          }
        })
      )
      .subscribe();
    this.vehicleId = this.route.snapshot.paramMap.get('id');
    this.pageTitle.set(this.changeCoverageText);
  }
  goBackOneStep() {
    this.store.dispatch(fromRouterActions.Back());
  }

  goToOverview() {
    this.store.dispatch(
      fromRouterActions.Go({
        path: ['/overview']
      })
    );
  }

  coverageReducer(
    coverages: ICoverage[] | PolicyCoverage[],
    isPolicy = false,
    isPartner: boolean,
    stateCode: string,
    initCoverages?: ICoverage[] | PolicyCoverage[]
  ) {
    return coverages.map((c: ICoverage | PolicyCoverage) => {
      const isCheckbox = this.changeCoverageFeatureFacade.isCheckBox(c);
      let selectedValue = '';
      const hasChanged = initCoverages
        ? this.changeCoverageFeatureFacade.isCoverageUpdated(initCoverages, c)
        : false;
      if (isCheckbox) {
        if (c.terms.length > 0 && c.selected) {
          selectedValue = this.policySharedService.getSelectedValue(c, isPartner, stateCode);
        } else
          selectedValue =
            c.selected || (c.terms.length && c.terms[0].chosenTermValue)
              ? 'Included'
              : 'Not Included';
      } else {
        if (c.selected) {
          selectedValue = new CoverageCurrencyUSDPipe().transform(
            c.terms[0].chosenTermValue.toString(),
            c.publicID
          );

          if (isPolicy) {
            if ((c as PolicyCoverage).amount?.currency) {
              const convertedTermValues = selectedValue.split('/');
              const termNames = c.terms[0].name.split('/');
              let newSelectedValue = '';
              for (let i = 0; i < convertedTermValues.length; i++) {
                newSelectedValue += `${convertedTermValues[i]} ${termNames[i]}, `;
              }
              selectedValue = newSelectedValue.replace(/,\s*$/, '');
            } else {
              selectedValue += ' ' + c.terms[0].name;
            }
          }
        } else {
          selectedValue = 'Not Included';
        }
      }

      return {
        option: c.name,
        value: selectedValue,
        isCheckbox: isCheckbox,
        coverageHelpTextList: [''],
        highlight: hasChanged
      };
    });
  }

  notifyAgent() {
    this.changeCoverageFeatureFacade.sendToAgent(this.vehicleId);
    if (!this.isPartner) {
      this.coveageChangeFeatureFacade.sendAnalytics({
        buttonAnalyticName: 'Quote:Cancel',
        event: FEATURES.CHANGE
      });
    }
  }
}
